import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { providerPlanStatementSelector } from "src/redux/selectors/custom-selector";
import {
  applyKpiSummarySheetFormatting,
  flattenExportColumns,
  getExportCellFormat,
  getExportSummaryData,
  getKpiSummaryFooterValue,
} from "../planCompStatementReport/excelExportSectionOneUtils";

const ExcelExportProviderSectionOne = forwardRef((props, ref) => {
  const providerExportRef = useRef(null);
  const kpiExportRef = useRef(null);
  const { providerPlanSummaryHeader, providerKpiPaymentSummary } = useSelector(
    providerPlanStatementSelector,
    shallowEqual
  );

  const providerColumns = flattenExportColumns(providerPlanSummaryHeader.columns);
  const kpiColumns = flattenExportColumns(providerKpiPaymentSummary.columns);
  const providerExportData = getExportSummaryData(providerColumns, providerPlanSummaryHeader.data);

  const buildWorkbookOptions = () => {
    const sheets = [];

    if (providerExportRef.current && providerPlanSummaryHeader.data?.length) {
      const providerSheet = providerExportRef.current.workbookOptions().sheets[0];
      providerSheet.name = "Provider Summary";
      sheets.push(providerSheet);
    }

    if (kpiExportRef.current && providerKpiPaymentSummary.data?.length) {
      const kpiSheet = kpiExportRef.current.workbookOptions().sheets[0];
      kpiSheet.name = "KPI Payment Summary";
      applyKpiSummarySheetFormatting(kpiSheet, providerKpiPaymentSummary.data, kpiColumns);
      sheets.push(kpiSheet);
    }

    return { sheets };
  };

  const saveWorkbook = (workbook, fileName = "Provider_Comp_Statement.xlsx") => {
    const saver = providerExportRef.current || kpiExportRef.current;
    saver?.save(workbook, fileName);
  };

  useImperativeHandle(
    ref,
    () => ({
      getWorkbookOptions: buildWorkbookOptions,
      saveWorkbook,
      excelExport: () => {
        const workbook = buildWorkbookOptions();
        if (workbook.sheets.length) {
          saveWorkbook(workbook);
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [providerPlanSummaryHeader, providerKpiPaymentSummary, providerColumns, kpiColumns]
  );

  return (
    <>
      {providerExportData?.length > 0 && (
        <ExcelExport
          ref={providerExportRef}
          data={providerExportData}
          fileName="Provider_Comp_Statement.xlsx"
        >
          {providerColumns.map((column) => (
            <ExcelExportColumn
              key={column.field}
              field={column.field}
              title={column.title}
              width={130}
              headerCellOptions={{ bold: true }}
              cellOptions={{ format: getExportCellFormat(column, providerPlanSummaryHeader.data) }}
            />
          ))}
        </ExcelExport>
      )}
      {providerKpiPaymentSummary.data?.length > 0 && (
        <ExcelExport
          ref={kpiExportRef}
          data={providerKpiPaymentSummary.data}
          fileName="Provider_Comp_Statement.xlsx"
        >
          {kpiColumns.map((column, index) => (
            <ExcelExportColumn
              key={column.field || index}
              field={column.field}
              title={column.title}
              width={130}
              headerCellOptions={{ bold: true, textAlign: "center" }}
              footerCellOptions={{ bold: true, textAlign: "center", background: "#7AC143" }}
              footer={() =>
                index === 0 ? "Total" : getKpiSummaryFooterValue(providerKpiPaymentSummary.data, column)
              }
              cellOptions={{
                textAlign: "center",
                format:
                  column.type === "number" && column.format
                    ? column.format.replace("{0:", "").replace("}", "")
                    : undefined,
              }}
            />
          ))}
        </ExcelExport>
      )}
    </>
  );
});

export default ExcelExportProviderSectionOne;
